import React, { useState, useEffect, useCallback } from "react";
import { useCreateItem } from "../../hooks/useItems";
import { useToastStore } from "../../stores/toastStore";
import { ManualAddModal } from "../modal/ManualAddModal";
import { authFetch } from "../../lib/authFetch";
import { formatPrice } from "../../lib/currencyUtils";

// Fallback placeholder for failed images
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f0f0f0' width='200' height='200'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui' font-size='14' fill='%23999'%3ENo image%3C/text%3E%3C/svg%3E";

interface Props {
  url: string;
  onClose: () => void;
  defaultListId?: string | null;
}

interface Preview {
  title: string;
  description?: string;
  image?: string;
  price?: number;
  currency?: string;
  merchant?: string;
  favicon?: string;
  error?: string;
  errorType?: string;
}

export const LinkPreviewCard: React.FC<Props> = ({ url, onClose, defaultListId }) => {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [isManualAddModalOpen, setIsManualAddModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const createItemMutation = useCreateItem();
  const showToast = useToastStore((s) => s.show);

  // Reset image error when preview changes
  useEffect(() => {
    setImageError(false);
  }, [preview?.image]);

  // Handle image load error
  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const scrape = async () => {
    setLoading(true);
      setPreview(null);
    
      try {
        // Use authFetch to ensure we have a fresh token
        const response = await authFetch("scrape-url", {
          method: "POST",
          body: JSON.stringify({ url }),
          signal: controller.signal
        });

        const data = await response.json();

        if (!controller.signal.aborted) {
          // Map function response to preview format
          const previewData = {
            title: data.title || url,
            description: data.description,
            image: data.image_url,
            price: data.price,
            currency: data.currency || "USD",
            merchant: data.merchant_name,
            favicon: data.favicon,
            ...(data.error && {
              error: data.error,
              errorType: data.errorType || "unknown"
            })
          };
          
          setPreview(previewData);
          setLoading(false);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          const errorMessage = err instanceof Error ? err.message : "Network error";
          setPreview({ 
            title: url,
            error: "Unable to load preview. You can still add the link manually.",
            errorType: "network_error"
          });
          setLoading(false);
        }
      }
    };

    scrape();

    return () => controller.abort();
  }, [url]);

  const handleAdd = () => {
    if (!preview) return;
    
    // Capture data before closing
    const itemData = {
      listId: defaultListId || null,
      name: preview.title,
      url,
      description: preview.description,
      price: preview.price,
      currency: preview.currency,
      imageUrl: preview.image,
      merchantName: preview.merchant,
      brandName: preview.merchant
    };
    
    // Close immediately for snappy UX
    onClose();
    
    // Add in background (fire and forget)
    createItemMutation.mutate(itemData, {
      onSuccess: () => showToast({ title: "Item added", type: "success" }),
      onError: () => showToast({ title: "Failed to add", type: "error" })
    });
  };

  const getFavicon = () => {
    try {
      return preview?.favicon || `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=64`;
    } catch {
      return "";
    }
  };

  // Check if scraping failed - has error OR only has URL as title with no other meaningful data
  const hasScrapingFailed = () => {
    if (!preview) return false;
    
    // If there's an error, it failed
    if (preview.error) return true;
    
    // If title is just the URL (or very similar), check if we have any other data
    const titleIsUrl = preview.title === url || preview.title.trim() === url.trim();
    
    // Count how many fields we successfully extracted (excluding title and url)
    const extractedFields = [
      preview.description,
      preview.image,
      preview.price,
      preview.merchant
    ].filter(Boolean).length;
    
    // If title is just the URL and we extracted 0 other fields, it failed
    if (titleIsUrl && extractedFields === 0) return true;
    
    return false;
  };

  const handleManualAdd = () => {
    setIsManualAddModalOpen(true);
  };

  return (
    <>
      {!isManualAddModalOpen && (
        <div className="ob-link-preview-card">
          {loading ? (
            <div className="ob-link-preview-loading">Loading preview...</div>
          ) : (
            <>
          {!hasScrapingFailed() && (
            <div className="ob-link-preview-image">
              {preview?.image && !imageError ? (
                <img 
                  src={preview.image} 
                  alt={preview.title || ""} 
                  onError={handleImageError}
                  loading="lazy"
                />
              ) : (
                <div className="ob-link-preview-image-placeholder">No image</div>
              )}
            </div>
          )}
          <div className="ob-link-preview-content">
                <h3 className="ob-link-preview-title">{preview?.title}</h3>
                
                {preview?.error && (
                  <div className="ob-link-preview-error">
                    We couldn't pull any information from that link. You can manually input information.
                  </div>
                )}
                
                {preview?.description && (
                  <p className="ob-link-preview-description">{preview.description}</p>
            )}
            <div className="ob-link-preview-meta">
                  {preview?.price && (
                <div className="ob-link-preview-price">
                      {formatPrice(preview.price, preview.currency)}
                </div>
              )}
                  {preview?.merchant && (
                <div className="ob-link-preview-merchant">
                      <img src={getFavicon()} alt="" className="ob-link-preview-favicon" />
                      <span>{preview.merchant}</span>
                </div>
              )}
            </div>
          </div>
          <div className="ob-link-preview-actions">
                <button className="ob-button ob-button-ghost" onClick={onClose}>
              Cancel
            </button>
                {hasScrapingFailed() ? (
                  <button className="ob-button ob-button-primary" onClick={handleManualAdd}>
                    Manually Add
                  </button>
                ) : (
                  <button className="ob-button ob-button-primary" onClick={handleAdd}>
                    Add
                  </button>
                )}
          </div>
            </>
          )}
        </div>
      )}
      
      <ManualAddModal
        isOpen={isManualAddModalOpen}
        onClose={() => {
          setIsManualAddModalOpen(false);
        }}
        onSuccess={() => {
          setIsManualAddModalOpen(false);
          onClose();
        }}
        url={url}
        defaultListId={defaultListId}
        initialData={preview ? {
          title: preview.title,
          description: preview.description,
          price: preview.price,
          currency: preview.currency,
          image: preview.image,
          merchant: preview.merchant
        } : undefined}
      />
    </>
  );
};
